<script lang="ts" module>
  import { ModalState } from '~/lib/nav.svelte';
  import { PersistedState } from 'runed';

  export const syncModal = new ModalState('sync');
  // null = never synced on this device (new device); number = ms timestamp of last known sync
  export const lastSyncedAt = new PersistedState<number | null>('classic-last-synced-at', null);
  // whether this device should auto-upload on idle/navigate
  export const syncEnabled = new PersistedState<boolean>('classic-sync-enabled', true);
</script>

<script lang="ts">
  import Modal from '~/components/Modal.svelte';
  import ActionButton from '~/components/ActionButton.svelte';
  import CloudDownload from '~icons/mdi/cloud-download';
  import CloudUpload from '~icons/mdi/cloud-upload';
  import CloudOff from '~icons/mdi/cloud-off';
  import Entry from '~/leaderboard/Entry.svelte';
  import type { ClassicGame } from '~/store/classic.svelte';
  import type { SLeaderboardEntry } from '~/types';
  import { restoreClassicSnapshot, saveClassicSnapshot } from '~/lib/mutations.svelte';
  import { getGame, setGame } from '~/store/GameContext.svelte';
  import { classicSnapshotQuery, isPremium } from '~/lib/queries.svelte';
  import Spinner from '~/components/Spinner.svelte';
  import { toast } from 'svelte-sonner';
  import { untrack } from 'svelte';

  const game = $derived(getGame<ClassicGame>().current);

  const set = setGame();
  const premium = isPremium();
  const cloudSnapshot = classicSnapshotQuery();
  const restore = restoreClassicSnapshot();
  const overwrite = saveClassicSnapshot(lastSyncedAt);

  const snapshot = $derived(cloudSnapshot.data);

  const isPending = $derived(overwrite.isPending || restore.isPending);

  type SyncOption = 'restore' | 'overwrite' | 'notNow';
  let selectedOption = $state<SyncOption>('restore');

  // Show sync modal once per load when cloud has changes since last sync
  let syncChecked = $state(false);
  $effect(() => {
    const snap = cloudSnapshot.data;
    const isLoading = cloudSnapshot.isPending || cloudSnapshot.isFetching;
    const isPrem = premium.data;
    if (isLoading || syncChecked || snap === undefined || isPrem === undefined) return;
    syncChecked = true;
    if (!isPrem || !snap) return;
    const cloudTime = new Date(snap.edited_at).getTime();
    // TODO is the time comparison flipped?
    const cloudIsNewer = cloudTime > (lastSyncedAt.current ?? 0);
    const isConflict = cloudIsNewer && (snap.swaps !== game.swaps || snap.score !== game.score);
    // potentially need to debounce?
    if (isConflict) {
      syncModal.push();
    } else if (cloudIsNewer) {
      lastSyncedAt.current = cloudTime;
    }
  });

  // Re-check for cloud conflicts when the user re-enables sync in Settings
  let syncWasEnabled = syncEnabled.current;
  $effect(() => {
    const enabled = syncEnabled.current;
    if (enabled && !syncWasEnabled) {
      untrack(() => {
        syncChecked = false;
        cloudSnapshot.refetch();
      })
    }
    syncWasEnabled = enabled;
  });

  const handleRestore = async () => {
    if (!snapshot) return;
    lastSyncedAt.current = new Date(snapshot.edited_at).getTime();
    syncEnabled.current = true;
    const restored = await restore.mutateAsync(snapshot.id);
    set(restored);
    syncModal.pop();
    toast.success('Cloud save restored');
  };

  const handleOverwrite = async () => {
    await overwrite.mutateAsync({ game, overwrite: true });
    lastSyncedAt.current = Date.now();
    syncEnabled.current = true;
    syncModal.pop();
    toast.success('Cloud save updated');
  };

  const handleNotNow = () => {
    if (snapshot) {
      lastSyncedAt.current = new Date(snapshot.edited_at).getTime();
    }
    syncEnabled.current = false;
    syncModal.pop();
    toast.info('Cloud sync can be re-enabled in settings');
  };

  const handleSubmit = async () => {
    if (selectedOption === 'restore') await handleRestore();
    else if (selectedOption === 'overwrite') await handleOverwrite();
    else handleNotNow();
  };

  const cloudEntry = $derived<SLeaderboardEntry>({
    id: snapshot?.id ?? '',
    userId: null,
    gameId: snapshot?.id ?? '',
    userName: 'Cloud',
    score: snapshot?.score ?? 0,
    bestStreak: 0,
    bestWord: snapshot?.latest_word?.word ?? [],
    numWords: 0,
    numTurns: 0,
    date: snapshot?.edited_at ?? '',
    rank: 0,
    day_rank: 0,
    week_rank: 0,
    all_rank: 0,
    local_rank: 0,
    swaps: snapshot?.swaps ?? undefined,
  });

  const deviceEntry = $derived<SLeaderboardEntry>({
    id: game.id,
    userId: null,
    gameId: game.id,
    userName: 'This device',
    score: game.score,
    bestStreak: 0,
    bestWord: game.latestWord?.word ?? [],
    numWords: game.words,
    numTurns: 0,
    date: new Date(game.lastTurnTimestamp).toISOString(),
    rank: 0,
    day_rank: 0,
    week_rank: 0,
    all_rank: 0,
    local_rank: 0,
    swaps: game.swaps,
  });
</script>

{#if snapshot && cloudSnapshot.isSuccess}
  <Modal open={syncModal.open} onClose={handleNotNow} index={2}>
    {#snippet title()}
      <CloudDownload size="1.5em" />
      Cloud Save Found
    {/snippet}
    {#snippet content()}
      <div class="comparison">
        <Entry tab="all" entry={cloudEntry}
          variant={selectedOption === 'restore' ? 'replacing' : selectedOption === 'overwrite' ? 'replaced' : undefined}
        />

        <div class="sync-icon" class:upload={selectedOption === 'overwrite'} class:offline={selectedOption === 'notNow'}>
          {#if selectedOption === 'overwrite'}
            <CloudUpload size="2em" />
          {:else if selectedOption === 'notNow'}
            <CloudOff size="2em" />
          {:else}
            <CloudDownload size="2em" />
          {/if}
        </div>

        {#if game.score > 0}
          <Entry tab="all" entry={deviceEntry}
            variant={selectedOption === 'overwrite' ? 'replacing' : selectedOption === 'restore' ? 'replaced' : undefined}
          />
        {/if}

        <div class="radio-options">
          <label class="radio-label" class:selected={selectedOption === 'restore'}>
            <input type="radio" name="sync-option" value="restore" bind:group={selectedOption} />
            <CloudDownload size="1.1em" />
            Restore
          </label>

          {#if game.score > 0}
            <label class="radio-label" class:selected={selectedOption === 'overwrite'}>
              <input type="radio" name="sync-option" value="overwrite" bind:group={selectedOption} />
              <CloudUpload size="1.1em" />
              Overwrite
            </label>
          {/if}

          <label class="radio-label" class:selected={selectedOption === 'notNow'}>
            <input type="radio" name="sync-option" value="notNow" bind:group={selectedOption} />
            <CloudOff size="1.1em" />
            Not now
          </label>
        </div>

        {#if selectedOption === 'restore'}
          <p class="hint">Replace your local game with the cloud save.</p>
        {:else if selectedOption === 'overwrite'}
          <p class="hint">Replace the cloud save with your local game.</p>
        {:else}
          <p class="hint">Keeps your local game. Re-enable sync anytime in Settings.</p>
        {/if}
      </div>
    {/snippet}
    {#snippet controls()}
      <ActionButton onClick={handleSubmit} disabled={isPending}>
        {#if selectedOption === 'restore'}
          <CloudDownload size="1em" />
          {#if isPending}
            Restoring<div class='spinner'><Spinner /></div>
          {:else}
            Restore cloud save
          {/if}
         {:else if selectedOption === 'overwrite'}
          <CloudUpload size="1em" />
          {#if isPending}
            Overwriting<div class='spinner'><Spinner /></div>
          {:else}
            Overwrite cloud save
          {/if}
        {:else}
          <CloudOff size="1em" />
          Skip for now
        {/if}
      </ActionButton>
    {/snippet}
  </Modal>
{/if}

<style lang="scss">
  @use 'sass:color';

  .comparison {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    width: 100%;
    padding: 0.5em 0;
  }

  .sync-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5em 0;
    opacity: 0.7;
    color: hsl(213, 56%, 40%);

    &.upload {
      color: hsl(160, 50%, 35%);
    }

    &.offline {
      color: hsl(0, 0%, 50%);
    }

    :global(html.dark-mode) & {
      color: hsl(213, 56%, 70%);

      &.upload {
        color: hsl(160, 50%, 60%);
      }

      &.offline {
        color: hsl(0, 0%, 60%);
      }
    }
  }

  .radio-options {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5em;
    margin: 1em 0 0.25em;
    width: 100%;
  }

  .radio-label {
    $color: hsl(213, 56%, 71%);
    display: flex;
    align-items: center;
    gap: 0.4em;
    cursor: pointer;
    font-weight: bold;
    font-size: 0.85em;
    text-transform: uppercase;
    padding: 0.5em 0.9em;
    border-radius: 999px;
    border: 2px solid #2f3640;
    background-color: color.scale($color, $lightness: 80%);
    color: #2f3640;
    transition: all 0.15s ease;
    user-select: none;

    input[type='radio'] {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
      pointer-events: none;
    }

    &:hover {
      background-color: color.scale($color, $lightness: 40%);
    }

    &.selected {
      background-color: color.scale($color, $lightness: 20%);
      border-color: hsl(213, 56%, 30%);
      color: hsl(213, 56%, 20%);
    }

    :global(html.dark-mode) & {
      $dark: #2f3640;
      color: white;
      background-color: $dark;
      border-color: white;

      &:hover {
        background-color: color.scale($dark, $lightness: 10%);
      }

      &.selected {
        background-color: hsl(213, 30%, 40%);
        border-color: hsl(213, 56%, 70%);
      }
    }
  }

  .hint {
    font-size: 0.75em;
    opacity: 0.6;
    text-align: center;
    margin: 0.5em 0 0;
    width: 100%;
  }

  .spinner {
  height: 1em;
  display: flex;
  align-items: center;
  }
</style>
